import { getMessage } from '@limitless-minds/app-messages'
import { Paragraph } from '@limitless-minds/ui-text'
import { Box, Stack, styled, useTheme } from '@mui/material'
import React, { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'

import { useResendCode } from '@/data/auth/hooks/useResendCode'
import { useStore } from '@/store'
import { Error as IError } from '@/types/Error'
import ErrorMessage from '@/ui/atoms/ErrorMessage'
import StyledLink from '@/ui/atoms/StyledLink'
import Footer from '@/ui/molecules/Footer'
import HeaderWrapper from '@/ui/molecules/HeaderWrapper'
import SignMethod from '@/ui/molecules/SignMethod'
import Body from '@/ui/organisms/Body'
import LoginForm from '@/ui/organisms/login/LoginForm'
import MFAForm from '@/ui/organisms/mfa/MFAform'

const StyledDiv = styled(Stack)(`
  justify-content: center;
  align-items: center;
  align-self: center;
  width: 100%;
  max-width: 25rem;
  height: 100%;
  gap: 2rem;
`)

const formatTime = (seconds: number): string => {
  const m = Math.floor(seconds / 60)
    .toString()
    .padStart(2, '0')
  const s = (seconds % 60).toString().padStart(2, '0')
  return `${m}:${s}`
}

export const LoginTemplate: React.FC = () => {
  const [activeStep, setActiveStep] = useState(0)
  const [timeLeft, setTimeLeft] = useState(0)
  const [resendErrorMessage, setResendErrorMessage] = useState('')
  const { email } = useStore()
  const { mutate: resendCode } = useResendCode()

  const theme = useTheme()
  const { clearAll } = useStore()

  const handleStepUp = useCallback((): void => {
    setActiveStep((prev) => prev + 1)
  }, [])

  const handleBackStep = useCallback((): void => {
    setActiveStep((prev) => prev - 1)
  }, [])

  const handleResendCode = (): void => {
    resendCode(
      {},
      {
        onSuccess: (response: any) => {
          setResendErrorMessage('')
          setTimeLeft(response.data.resendCooldownMinutes * 60)
        },
        onError: (error) => {
          const errorResponse = error as IError
          if (errorResponse.response?.status === 401) {
            setResendErrorMessage('')
            setActiveStep(0)
            return
          }
          const errorMessage = getMessage(
            errorResponse.response?.data?.error?.errorCode,
            errorResponse.response?.data?.error?.params
          )
          setResendErrorMessage(errorMessage)
        },
      }
    )
  }

  const steps = [
    <StyledDiv key={'login-step1'}>
      <HeaderWrapper title='Welcome Back.' subtitle='TRAIN YOUR MIND' />
      <SignMethod
        showTerms={false}
        onGoogleClick={handleStepUp}
        onMicrosoftClick={handleStepUp}
        onEmailClick={handleStepUp}
      />
    </StyledDiv>,

    <StyledDiv key={'login-step2'}>
      <HeaderWrapper title='Email Login' description='Enter the email address associated with your account.' />
      <LoginForm setShowMfaForm={setActiveStep} setCodeResetTimeOut={setTimeLeft} />
      <Link to='/forgot-password'>
        <Paragraph variant='p16' underlined color={theme.palette.role.primary}>
          Forgot your password?
        </Paragraph>
      </Link>
    </StyledDiv>,
    <StyledDiv key={'login-step3'}>
      <HeaderWrapper title='Verification code sent' description={`We sent a verification code to ${email}`} />
      <MFAForm goBackToLogin={setActiveStep} />
      {resendErrorMessage !== '' && <ErrorMessage>{resendErrorMessage}</ErrorMessage>}

      <Box onClick={handleResendCode} style={{ cursor: 'pointer' }}>
        <Paragraph
          variant='p16'
          underlined
          color={timeLeft > 0 ? theme.palette.role.onSurfaceMuted : theme.palette.role.primary}
        >
          {timeLeft > 0 ? `Request a new code in ${formatTime(timeLeft)}` : 'Request a new code'}
        </Paragraph>
      </Box>
    </StyledDiv>,
  ]

  useEffect(() => {
    if (timeLeft <= 0) return

    const interval = setInterval(() => {
      setTimeLeft((prev) => prev - 1)
    }, 1000)

    return (): void => clearInterval(interval)
  }, [timeLeft])

  return (
    <>
      <Body steps={steps} handleBackStep={handleBackStep} activeStep={activeStep} />
      <Footer>
        Don&apos;t have an account?
        <StyledLink to='/sign-up' onClick={clearAll}>
          Sign Up
        </StyledLink>
      </Footer>
    </>
  )
}
